import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { tap, startWith, map, BehaviorSubject, combineLatest, Subscription, Observable, switchMap } from 'rxjs';

import { Site } from '@shared/models/site';
import { SortActivity } from '@shared/models/sort-activity';

import { ProductService } from '../../../../services/product.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { ShiftValidationService } from '../../../../services/shift-validation/shift-validation.service';
import { ComponentsSummary, ComponentsSummaryItem } from '../../../../models/shift-validation/component-summary';
import { SelectItem } from 'primeng/api';
import { ActivatedRoute } from '@angular/router';
import { url } from '@shared/helpers/url';

@Component({
    selector: 'app-components-summary',
    templateUrl: './components-summary.component.html',
    styleUrls: ['./components-summary.component.scss']
})
export class ComponentsSummaryComponent implements OnInit, OnDestroy {

    componentsSummary = new BehaviorSubject<ComponentsSummary>({ items: [] });
    loading: boolean = false;
    site: Site;
    subscriptions: Subscription[];
    startupSubscription: Subscription;
    isFilteredByRepairType = false;

    chepRepairTypes: SelectItem<SortActivity>[] = [];

    filters = new FormGroup({
        showAll: new FormControl<boolean>(false),
        repairType: new FormControl<SortActivity>(null)
    })

    filteredRows = combineLatest([
        this.componentsSummary,
        this.filters.valueChanges.pipe(startWith(this.filters.value))
    ]).pipe(map(([summary, filters]) => summary?.items
        .filter(i => filters.showAll || !this.allAreZero(i))
        .filter(i => !filters.repairType || i.sortActivity === filters.repairType) ?? []));

    constructor(
        private shiftContextService: ShiftContextService,
        private shiftValidationService: ShiftValidationService,
        private productService: ProductService,
        private route: ActivatedRoute) { }

    ngOnInit() {
        this.loading = true;
        const siteObservable = combineLatest([
            this.shiftContextService.CurrentContext,
            this.route.params
        ]).pipe(
            tap(([context, routeParams]) => {
                this.site = context.site;

                const subinventoryId = parseInt(routeParams.id, 10);
                const summary = this.shiftValidationService.CurrentMappedSummary<ComponentsSummary>(url`Components/${subinventoryId}`);

                if (summary)
                    summary.items.sort((a, b) => a.sortActivity - b.sortActivity);

                this.componentsSummary.next(summary);

                this.chepRepairTypes = [
                    { value: null, label: 'Show All'},
                    ...[...new Set(summary?.items.map(i => i.sortActivity))]
                        .map(i => ({ value: i, label: this.getType(i) }))
                ];

                this.loading = false;
            }));

        this.subscriptions = [
            siteObservable.subscribe()
        ];
    }

    allAreZero(obj: ComponentsSummaryItem): boolean {
        const temp = { ...obj, productId: 0, sortActivity: 0};
        return new Set(Object.values(temp).map(v => v ?? 0)).size === 1;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.componentsSummary.next({ items: [] });
    }

    getProduct(productId: number): Observable<string> {
        return this.productService.FindProduct(productId).pipe(map(p => p?.label));
    }

    getType(typeId: SortActivity): string {
        switch (typeId) {
            case SortActivity.PlusActivity:
                return 'Plus';
            case SortActivity.PremActivity:
                return 'Premium';
            case SortActivity.CustActivity:
                return 'Custom';
            case SortActivity.MulticolorRepairs:
                return 'Multicolor';
            default:
                return '';
        }
    }

    sumLumberTotal(selector: keyof ComponentsSummaryItem | ((item: ComponentsSummaryItem) => number)): Observable<number> {
        const func = typeof selector === 'string'
            ? (row: ComponentsSummaryItem) => row[selector]
            : selector;

        return combineLatest([this.componentsSummary, this.productService.CurrentProducts]).pipe(
            map(([summary, products]) => (summary?.items ?? [])
                .filter(item => products.find(p => p.id === item.productId)?.productType === 'Component')
                .map(func)
                .reduce((agg, cur) => agg + cur, 0)));
    }

    sumTotal(selector: keyof ComponentsSummaryItem | ((item: ComponentsSummaryItem) => number)): Observable<number> {
        const func = typeof selector === 'string'
            ? (row: ComponentsSummaryItem) => row[selector]
            : selector;

        return this.componentsSummary.pipe(map(summary => (summary?.items ?? [])
            .map(func)
            .reduce((agg, cur) => agg + cur, 0)));
    }

    lastStep() {
        return false;
    }
}
